import React, { useState } from 'react'
import axios from 'lib/axios'
import { useSelector } from 'react-redux'
import { PDFDocument } from 'pdf-lib'
import clsx from 'clsx'
import TaskItem from '../TaskItem'

const MAX_ATTEMPTS = 3
const POLL_INTERVAL_MS = 2000

export const generatePdfFromPages = async (imageUrls) => {
  try {
    const pdfDoc = await PDFDocument.create()

    for (const url of imageUrls) {
      try {
        // Fetch resource from URL
        const response = await fetch(url)
        const contentBytes = await response.arrayBuffer()

        // Determine content type
        const contentType = response.headers.get('content-type')
        const urlLower = url.toLowerCase()

        // Handle PDF files
        if (contentType?.includes('pdf') || urlLower.endsWith('.pdf')) {
          // Load the PDF
          const existingPdfDoc = await PDFDocument.load(contentBytes)

          // Copy all pages from the PDF
          const copiedPages = await pdfDoc.copyPages(
            existingPdfDoc,
            existingPdfDoc.getPageIndices()
          )

          // Add each copied page to the main document
          copiedPages.forEach((page) => {
            pdfDoc.addPage(page)
          })
        }
        // Handle PNG images
        else if (contentType?.includes('png') || urlLower.endsWith('.png')) {
          const image = await pdfDoc.embedPng(contentBytes)
          const { width, height } = image.scale(1)
          const page = pdfDoc.addPage([width, height])
          page.drawImage(image, {
            x: 0,
            y: 0,
            width: width,
            height: height,
          })
        }
        // Handle JPEG images
        else if (
          contentType?.includes('jpeg') ||
          contentType?.includes('jpg') ||
          urlLower.endsWith('.jpg') ||
          urlLower.endsWith('.jpeg')
        ) {
          const image = await pdfDoc.embedJpg(contentBytes)
          const { width, height } = image.scale(1)
          const page = pdfDoc.addPage([width, height])
          page.drawImage(image, {
            x: 0,
            y: 0,
            width: width,
            height: height,
          })
        }
        // Unsupported format
        else {
          console.warn(`Unsupported file type for URL: ${url}`)
          continue
        }
      } catch (error) {
        console.error(`Error processing file: ${url}`, error)
      }
    }

    // Save and download PDF
    const pdfBytes = await pdfDoc.save()
    const blob = new Blob([pdfBytes], { type: 'application/pdf' })
    const url = URL.createObjectURL(blob)

    const link = document.createElement('a')
    link.href = url
    link.download = 'document.pdf' // Updated name
    link.target = '_blank'
    link.click()

    URL.revokeObjectURL(url)
  } catch (error) {
    console.error('Error generating PDF:', error)
  }
}

export default function DownloadTask({ disabled = false }) {
  const blankFormUrl = useSelector((state) => state.global.questionnaireForm.url)
  const hashedId = useSelector((state) => state.global.hashedId)
  const respondedFiles = useSelector((state) => state.global.respondingFiles)

  const [polledUrl, setPolledUrl] = useState(null)
  const [polling, setPolling] = useState(false)

  const effectiveUrl = blankFormUrl || polledUrl

  const download = async () => {
    if (effectiveUrl) {
      window.open(effectiveUrl, '_blank')
      return
    }

    if (polling || !hashedId) return

    let attempts = 0
    setPolling(true)

    const intervalId = setInterval(async () => {
      attempts++
      let done = false
      try {
        const { data } = await axios.get(`/questionnaire_requests/${hashedId}/blank_form_url`)
        if (data.url) {
          setPolledUrl(data.url)
          window.open(data.url, '_blank')
          done = true
        }
      } catch (_) {
      } finally {
        if (done || attempts >= MAX_ATTEMPTS) {
          clearInterval(intervalId)
          setPolling(false)
        }
      }
    }, POLL_INTERVAL_MS)
  }

  const isDisabled = disabled || polling

  return (
    <TaskItem
      disabled={isDisabled}
      title="download"
      subTitle="to complete form"
      btnTitle={<i className={polling ? 'fa-light fa-spinner fa-spin' : 'fa-light fa-download'} />}
      iconClassName="fa-light fa-signature"
      submitBtnClasses={clsx(
        'dome-btn dome-btn-base',
        respondedFiles.length > 0 ? 'dome-btn-grey-stroke' : 'dome-btn-green-stroke'
      )}
      onSubmit={download}
    />
  )
}
